import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsDatepickerConfig, BsLocaleService } from 'ngx-bootstrap/datepicker';
import {
  defineLocale,
  enGbLocale,
  esLocale,
  frLocale,
} from 'ngx-bootstrap/chronos';
import { SummaryService } from '../../../../../core/services/summary.service';
import { ISummaryData } from '../../../../../core/models/summary-data';
import { formatDate } from '@angular/common';
import { ProductAvailabilityService } from '../../../../../core/services/product-availability.service';
import {
  ConcatenatedHour,
  HoursAvailabilityResponse,
} from '../../../../../core/interfaces/funnel.interface';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from '../../../../../core/services/loader.service';
import { FunnelService } from '../funnel.service';
import { PersonalizationId } from '../../../../../core/enums/catalog.enum';

interface CalendarDay {
  date: Date;
  day: number;
  otherMonth: boolean;
  selected: boolean;
}

@Component({
  selector: 'app-summary',
  standalone: false,
  templateUrl: './summary.component.html',
  styleUrl: './summary.component.scss',
})
export class SummaryComponent implements OnInit {
  @ViewChild('selectWrapper') selectWrapper!: ElementRef;
  @ViewChild('customSelect') customSelect!: ElementRef;
  @ViewChild('pickUpListener') pickUpListener!: ElementRef;
  @ViewChild('endHourListener') endHourListener!: ElementRef;
  @ViewChild('pickUpTimeSelector') pickUpTimeSelector!: ElementRef;
  @ViewChild('returnTimeSelector') returnTimeSelector!: ElementRef;
  @ViewChild('pickHourPairSelector') pickHourPairSelector!: ElementRef;

  @Output() sendSummaryData: EventEmitter<any> = new EventEmitter<any>();

  contactForm!: FormGroup;
  today: Date = new Date();
  bsConfig: Partial<BsDatepickerConfig> = {};
  productId: string = '';
  productBlockedDates: any[] = [];
  summaryData: ISummaryData = {} as ISummaryData;
  formattedStartTime: string = '';
  formattedEndTime: string = '';
  availableStartHours: HoursAvailabilityResponse[] = [];
  availableEndHours: HoursAvailabilityResponse[] = [];
  availableHours: ConcatenatedHour[] = [];
  selectedDate: Date = new Date();
  selectedReturnDate: Date = new Date();
  isReturn: boolean = false;
  isSpecificHours: boolean = false;
  isReturnSpecificHours = false;
  isPickupTimeDropdownOpen: boolean = false;
  isEndHourDropdownOpen: boolean = false;
  selectedStartHour = '';
  selectedEndHour = '';
  noAvailableEndHours = false;
  warningMessage = '';
  isSpecificHoursDropdownOpen = false;
  noAvailableHours = false;
  availableReturnHours: ConcatenatedHour[] = [];
  showSpecificHoursSection = false;
  showDropdown = false;
  selectedHourPair: ConcatenatedHour | null = null;
  originPhonePrefix: string = '+34';
  destinationPhonePrefix: string = '+34';
  sameContact = true;
  FixedFee: number = 0;
  placeholder = '';
  isValetDropdownOpen = false;
  valetStations: any[] = [];
  public pickupDateSelected = false;
  personalizationId = PersonalizationId;
  returnDate!: string;
  showReturnDropdown = false;
  selectedReturnHourPair: ConcatenatedHour | null = null;
  selectedDateForValetPickUp: Date = new Date();
  selectedDateForValetReturn: Date = new Date();
  formattedStartDate: string = '';
  formattedEndDate: string = '';
  isPrefixOpen = false;
  lastPrefixValue: string | null = null;

  constructor(
    private fb: FormBuilder,
    private localeService: BsLocaleService,
    private summaryService: SummaryService,
    private productAvailabilityService: ProductAvailabilityService,
    private translateService: TranslateService,
    private loader: LoaderService,
    private funnelService: FunnelService
  ) {
    defineLocale('es', { ...esLocale, week: { dow: 1, doy: 4 } });
    defineLocale('en', { ...enGbLocale, week: { dow: 1, doy: 4 } });
    defineLocale('fr', { ...frLocale, week: { dow: 1, doy: 4 } });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSpecificHours']) {
      this.getProductAvailability(this.productId, this.selectedDate);
    }
    if (changes['isReturnSpecificHours']) {
      this.getProductAvailability(
        this.productId,
        this.selectedReturnDate,
        true
      );
    }
  }

  ngOnInit() {
    this.summaryData = this.summaryService.getSummary() as ISummaryData;
    this.loadValetStations();
    console.log(this.valetStations);
    this.setPlaceholder();

    this.translateService.onLangChange.subscribe(() => {
      this.setPlaceholder();
    });

    this.showSpecificHoursSection = false;

    // this.contactForm.get('serviceDate')?.valueChanges.subscribe(val => {
    //   console.log('Fecha seleccionada:', val);
    // });

    if (this.summaryData) {
      this.productId = this.summaryData.productInformation?.product?.Id || '';
      console.log('productId', this.productId);
    }

    this.contactForm = this.fb.group({
      serviceDate: [null, Validators.required],
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
      sameContact: [true],
      valetStation: ['', Validators.required],
      originContact: this.fb.group({
        name: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        phone: ['', Validators.required],
        originPhonePrefix: [this.originPhonePrefix],
      }),
      destinationContact: this.fb.group({
        name: [''],
        lastName: [''],
        email: [''],
        phone: [''],
        destinationPhonePrefix: [this.destinationPhonePrefix],
      }),
      extraInstructions: [''],
    });

    this.contactForm
      .get('sameContact')
      ?.valueChanges.subscribe((same: boolean) => {
        const destContact = this.contactForm.get(
          'destinationContact'
        ) as FormGroup;

        if (same) {
          destContact.disable();
        } else {
          destContact.enable();
        }
      });

    // Inicializar correctamente según el valor del checkbox
    if (this.contactForm.get('sameContact')?.value) {
      this.contactForm.get('destinationContact')?.disable();
    }

    this.setDatePickerConfig();
  }

  submitForm() {
    const formValue = this.contactForm.value;

    if (this.isValet()) {
      this.formattedStartDate = this.formatDateTimeWithTimezone(
        this.selectedDate,
        this.selectedStartHour
      );
      this.formattedEndDate = this.formatDateTimeWithTimezone(
        this.selectedDateForValetReturn,
        this.selectedEndHour
      );
    } else {
      this.formattedStartDate = this.formatDateTimeWithTimezone(
        formValue.serviceDate,
        this.selectedStartHour
      );
      this.formattedEndDate = this.formatDateTimeWithTimezone(
        formValue.serviceDate,
        this.selectedEndHour
      );
    }

    const contactData = {
      originPhonePrefix: this.originPhonePrefix,
      destinationPhonePrefix: this.destinationPhonePrefix,
      isSameContact: formValue.sameContact,
      FixedFee: this.FixedFee,
      ServiceStartDate: this.formattedStartDate,
      ServiceEndDate: this.formattedEndDate,
      IsDestinationContactExternal: true,
      IsOriginContactExternal: true,
      originContactName: formValue.originContact.name,
      originContactLastName: formValue.originContact.lastName,
      originContactEmailAddress: formValue.originContact.email,
      originContactPhoneNumber: formValue.originContact.phone,
      destinationContactName: formValue.sameContact
        ? formValue.originContact.name
        : formValue.destinationContact.name,
      destinationContactLastName: formValue.sameContact
        ? formValue.originContact.lastName
        : formValue.destinationContact.lastName,
      destinationContactEmailAddress: formValue.sameContact
        ? formValue.originContact.email
        : formValue.destinationContact.email,
      destinationContactPhoneNumber: formValue.sameContact
        ? formValue.originContact.phone
        : formValue.destinationContact.phone,
      extraInstructions: formValue.extraInstructions || '',
      serviceConfiguration: { StationId: formValue.valetStation.StationId },
    };

    this.sendSummaryData.emit(contactData);
  }

  setDatePickerConfig(): void {
    const savedLanguage = localStorage.getItem('language');
    this.localeService.use(savedLanguage || 'es');
    this.bsConfig = {
      dateInputFormat: 'DD/MM/YYYY',
      containerClass: 'theme-default',
      showWeekNumbers: false,
      daysDisabled: [6, 0],
      minDate: this.today,
      datesDisabled: this.productBlockedDates,
      adaptivePosition: true,
      isAnimated: true,
    };
  }

  getProductAvailability(
    productId?: string,
    selectedDate: Date = new Date(),
    isReturn = false
  ) {
    if (!productId) return;
    this.noAvailableHours = false;

    this.loader.show();
    this.productAvailabilityService
      .getProductAvailability(
        productId,
        selectedDate,
        isReturn ? this.isReturnSpecificHours : this.isSpecificHours,
        isReturn
      )
      .subscribe({
        next: (hours: any) => {
          this.loader.hide();
          if (!isReturn) {
            if (!this.isSpecificHours) {
              this.availableStartHours = [...hours];
              this.availableEndHours = [...hours];
              if (this.availableStartHours.length > 0) {
                this.handleStartTimeChange(this.selectedStartHour);
              }
            } else {
              this.availableHours =
                this.productAvailabilityService.transformHours(hours);
            }
          } else {
            this.availableReturnHours =
              this.productAvailabilityService.transformHours(hours);
          }
          if (hours.length == 0) {
            this.noAvailableEndHours = true;
          }
        },
        error: (error: any) =>
          console.error('Error loading product availability', error),
      });
  }

  formatDateTimeWithTimezone(date: Date, startTime: string): string {
    const [hours, minutes] = startTime.split(':').map(Number);
    const adjustedDate = new Date(date);
    adjustedDate.setHours(hours, minutes, 0, 0);

    const year = adjustedDate.getFullYear();
    const month = (adjustedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = adjustedDate.getDate().toString().padStart(2, '0');
    const formattedHours = adjustedDate.getHours().toString().padStart(2, '0');
    const formattedMinutes = adjustedDate
      .getMinutes()
      .toString()
      .padStart(2, '0');

    const offsetMinutes = adjustedDate.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60))
      .toString()
      .padStart(2, '0');
    const offsetSign = offsetMinutes <= 0 ? '+' : '-';

    return `${year}-${month}-${day}T${formattedHours}:${formattedMinutes}:00${offsetSign}${offsetHours} Europe/Madrid`;
  }

  togglePickupTimeDropdown() {
    this.isPickupTimeDropdownOpen = !this.isPickupTimeDropdownOpen;
  }

  selectPickupTime(hour: HoursAvailabilityResponse) {
    const formValue = this.contactForm.value;
    formValue.startTime = hour.Text;
    this.selectedStartHour = hour.Text!;
    this.isPickupTimeDropdownOpen = false;
    this.handleStartTimeChange(hour);
  }

  handleStartTimeChange(event: any) {
    const result = this.productAvailabilityService.handleStartTimeChange(
      event,
      this.availableStartHours,
      this.summaryData
    );
    this.availableEndHours = result.availableEndHours;
    this.noAvailableEndHours = result.noAvailableEndHours;
    this.selectedEndHour = result.selectedEndHour;
    this.warningMessage = result.warningMessage;
  }

  handlePickupTimeOptionKeydown(
    event: KeyboardEvent,
    hour: HoursAvailabilityResponse
  ) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.selectPickupTime(hour);
      event.preventDefault();
    }
  }

  toggleEndHourDropdown() {
    if (!this.noAvailableEndHours) {
      this.isEndHourDropdownOpen = !this.isEndHourDropdownOpen;
    }
  }

  selectEndHour(hour: HoursAvailabilityResponse) {
    const formValue = this.contactForm.value;
    formValue.endTime = hour.Text;
    this.selectedEndHour = hour.Text!;
    this.isEndHourDropdownOpen = false;
  }

  toggleSpecificHoursDropdown() {
    this.isSpecificHoursDropdownOpen = !this.isSpecificHoursDropdownOpen;
  }

  selectSpecificHours(value: boolean) {
    console.log('selectSpecificHours', value);
    this.isSpecificHours = value;
    if (value) {
      this.FixedFee = 6;
    }
    this.isSpecificHoursDropdownOpen = false;
    this.onTimeSlotChange(value);
  }

  onTimeSlotChange(value: boolean) {
    this.isSpecificHours = value;
    Object.assign(
      this,
      this.productAvailabilityService.resetTimeSlotSelection()
    );
    this.getProductAvailability(this.productId, this.selectedReturnDate);
  }

  onReturnDateChange(event: Date): void {
    if (event) {
      this.showSpecificHoursSection = true;
    }
    // this.selectSpecificHours(false);
    this.selectedReturnDate = event;
    this.getProductAvailability(this.productId, this.selectedReturnDate, true);
  }

  onReturnDateChangeForValet(event: Date): void {
    this.selectedDateForValetReturn = event;
    if (event) {
      this.showSpecificHoursSection = true;
    }
    // this.selectSpecificHours(false);
    this.selectedReturnDate = event;
    this.getProductAvailability(this.productId, this.selectedReturnDate, true);
  }

  toggleDropdown() {
    console.log('toggleDropdown');
    this.showDropdown = !this.showDropdown;
    console.log('showDropdown', this.showDropdown);
  }

  selectHour(hour: ConcatenatedHour) {
    this.selectedStartHour = hour.start.Text;
    this.selectedEndHour = hour.end.Text;
    this.selectedHourPair = hour;
    this.showDropdown = false;
  }

  selectHourForValet(hour: ConcatenatedHour) {
    this.selectedHourPair = hour;
    this.selectedStartHour = hour.start.Text;
    this.showDropdown = false;
  }

  selectReturnHourForValet(hour: ConcatenatedHour) {
    this.selectedReturnHourPair = hour;
    this.selectedEndHour = hour.end.Text;
    this.showReturnDropdown = false;
  }

  showData() {
    console.log(this.contactForm.value);
  }

  toggleSameContact() {
    this.sameContact = !this.sameContact;
    this.contactForm.get('sameContact')?.setValue(this.sameContact);
  }

  private setPlaceholder(): void {
    this.translateService
      .get('additional-resources-info')
      .subscribe((text) => (this.placeholder = text));
  }

  isValet(): boolean {
    return (
      this.summaryData.productInformation?.product?.ProductKey ===
      'valet-station'
    );
  }

  isOpenForm(): boolean {
    return (
      this.summaryData.productInformation?.product?.ProductKey ===
      this.personalizationId.OpenForm
    );
  }

  toggleValetDropdown() {
    this.isValetDropdownOpen = !this.isValetDropdownOpen;
  }

  handleValetKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.toggleValetDropdown();
      event.preventDefault();
    }
  }

  loadValetStations() {
    if (this.isValet()) {
      this.loader.show();
      this.isSpecificHours = true;
      this.isReturnSpecificHours = true;
      this.funnelService
        .getValetStations(
          this.summaryData.productInformation?.product?.Id as string
        )
        .subscribe({
          next: (response: any) => {
            this.valetStations = response;
            console.log('valetStations', this.valetStations);
          },
          error: () => this.loader.hide(),
          complete: () => this.loader.hide(),
        });
    }
  }

  getSelectedValetStationName(): string {
    const selectedStation = this.contactForm.get('valetStation')?.value;
    return selectedStation ? selectedStation.StationName : '';
  }

  selectValetStation(station: any) {
    this.contactForm.get('valetStation')?.setValue(station);
    this.isValetDropdownOpen = false;
  }

  handleValetOptionKeydown(event: KeyboardEvent, station: any) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.selectValetStation(station);
      event.preventDefault();
    }
  }

  onPickupDateChange(event: Date): void {
    this.selectedDate = event;
    this.pickupDateSelected = !!event;
    this.getProductAvailability(
      this.summaryData.productInformation?.product?.Id ?? '',
      this.selectedDate
    );
    this.noAvailableEndHours = false;
  }

  onPickupDateChangeForValet(event: Date): void {
    this.selectedDateForValetPickUp = event;
    this.selectedDate = event;
    this.pickupDateSelected = !!event;
    this.getProductAvailability(
      this.summaryData.productInformation?.product?.Id ?? '',
      this.selectedDate
    );
    this.noAvailableEndHours = false;
  }

  toggleReturnDropdown() {
    this.showReturnDropdown = !this.showReturnDropdown;
  }

  selectReturnHour(hour: ConcatenatedHour) {
    this.selectedReturnHourPair = hour;
    this.showReturnDropdown = false;
  }

  onSelectClick(): void {
    const currentValue = this.contactForm.get('originPhonePrefix')?.value;

    if (this.isPrefixOpen && currentValue === this.lastPrefixValue) {
      // Si ya estaba abierto y no cambió el valor, ciérralo
      this.isPrefixOpen = false;
    } else {
      this.isPrefixOpen = true;
    }

    this.lastPrefixValue = currentValue;
  }

  onSelectChange(): void {
    setTimeout(() => {
      this.isPrefixOpen = false;
    }, 1);
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    if (
      this.selectWrapper &&
      !this.selectWrapper.nativeElement.contains(event.target)
    ) {
      this.isPrefixOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const clickedInsideCustom = this.customSelect?.nativeElement.contains(
      event.target
    );
    const clickedInsidePickup = this.pickUpListener?.nativeElement.contains(
      event.target
    );
    const clickedInsideEndHour = this.endHourListener?.nativeElement.contains(
      event.target
    );
    // const pickUpTimeSelector = this.pickUpTimeSelector?.nativeElement.contains(
    //   event.target
    // );
    // const returnTimeSelector = this.returnTimeSelector?.nativeElement.contains(
    //   event.target
    // );
    // const pickHourPairSelector =
    //   this.pickHourPairSelector?.nativeElement.contains(event.target);

    // if (!pickHourPairSelector) {
    //   this.showDropdown = false;
    // }
    // if (!returnTimeSelector) {
    //   this.showReturnDropdown = false;
    // }

    // if (!pickUpTimeSelector) {
    //   this.showDropdown = false;
    // }

    if (!clickedInsideCustom) {
      this.isValetDropdownOpen = false;
      this.isSpecificHoursDropdownOpen = false;
    }

    if (!clickedInsidePickup) {
      this.isPickupTimeDropdownOpen = false;
    }

    if (!clickedInsideEndHour) {
      this.isEndHourDropdownOpen = false;
    }
  }
}
